/**
 * Keyboard + pointer-lock mouse sampling.
 *
 * Produces the button bitfield and look angles that go into an input frame.
 * Mostly dumb: it holds current state, so that the question "what is the
 * player pressing" has exactly one answer at the moment a tick is built.
 *
 * The exception is taps, and it has to be. Fire and Use are *edges* on the
 * server (`resolveFireEdge`, and the Use equivalent beside it): the press is
 * what counts, and holding the button does nothing more. But ticks are 50ms
 * apart and a frame can be much longer than that on a slow machine, so a
 * mousedown and mouseup that both land between two samples of a purely
 * level-triggered sampler describe a click that never happened. The player
 * pressed the button and the game did not shoot — silently, and more often
 * the worse their frame rate is.
 *
 * So a press is *latched* until a tick has actually observed it. See
 * `sampleTick`, which is the only thing that clears one.
 */

import { Btn, PITCH_LIMIT } from "@prop-hunt/shared";

const DEFAULT_SENSITIVITY = 0.0022;

/**
 * Buttons whose press is an event rather than a state, and which must therefore
 * survive until a tick reads them.
 *
 * Fire and Use are edge-detected server-side. Jump is not — `movement.ts` tests
 * `onGround && Jump` every tick — but a tap that no tick ever saw is just as
 * dropped, so it belongs here for the same reason.
 *
 * Nothing else does. Movement and crouch are held states, and latching one
 * would make a key stick for a tick after it was let go.
 */
const TAP_BUTTONS = Btn.Fire | Btn.Use | Btn.Jump;

/**
 * Which button each key code presses.
 *
 * One table rather than a chain of `held.has(...)` tests, because the key→bit
 * mapping is now needed twice — once to report what is held, and once to decide
 * whether a keydown is a tap worth latching — and two copies of it would drift.
 */
const KEY_BITS: Readonly<Record<string, number>> = {
  KeyW: Btn.Forward,
  ArrowUp: Btn.Forward,
  KeyS: Btn.Back,
  ArrowDown: Btn.Back,
  KeyA: Btn.Left,
  ArrowLeft: Btn.Left,
  KeyD: Btn.Right,
  ArrowRight: Btn.Right,
  Space: Btn.Jump,
  ShiftLeft: Btn.Crouch,
  ControlLeft: Btn.Crouch,
  KeyE: Btn.Use,
};

export class InputSampler {
  yaw = 0;
  pitch = 0;
  sensitivity = DEFAULT_SENSITIVITY;

  private readonly held = new Set<string>();
  private firing = false;
  private locked = false;

  /**
   * Tap buttons pressed since the last tick sampled them, whether or not they
   * are still down. Cleared only by `sampleTick`.
   */
  private latched = 0;

  constructor(private readonly surface: HTMLElement) {}

  get pointerLocked(): boolean {
    return this.locked;
  }

  attach(): void {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onBlur);
    document.addEventListener("pointerlockchange", this.onPointerLockChange);
    document.addEventListener("mousemove", this.onMouseMove);
    this.surface.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mouseup", this.onMouseUp);
    this.surface.addEventListener("click", this.requestLock);
  }

  detach(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.onBlur);
    document.removeEventListener("pointerlockchange", this.onPointerLockChange);
    document.removeEventListener("mousemove", this.onMouseMove);
    this.surface.removeEventListener("mousedown", this.onMouseDown);
    window.removeEventListener("mouseup", this.onMouseUp);
    this.surface.removeEventListener("click", this.requestLock);
  }

  get buttons(): number {
    let bits = 0;
    for (const code of this.held) bits |= KEY_BITS[code] ?? 0;
    if (this.firing) bits |= Btn.Fire;
    return bits;
  }

  /**
   * The buttons for one simulation tick, including any tap that happened since
   * the last one and has already been released.
   *
   * The only caller is the fixed-timestep loop, and there must only ever be
   * one: this consumes the latch, so a second reader would either steal a
   * player's shot or duplicate it. Everything that just wants to know what is
   * held right now — the spectator camera, the crouch the camera renders at —
   * reads `buttons` instead and leaves the latch alone.
   *
   * A tap released before this ran contributes exactly one tick with the bit
   * set, and the next tick clears it, which is the clean press-then-release the
   * server's edge detection wants. Two taps between ticks coalesce into one:
   * the second is lost, which is the same answer a 20Hz simulation gives a
   * player who clicked twice in 50ms anyway.
   */
  sampleTick(): number {
    const bits = this.buttons | this.latched;
    this.latched = 0;
    return bits;
  }

  private readonly requestLock = (): void => {
    if (!this.locked) void this.surface.requestPointerLock();
  };

  private readonly onPointerLockChange = (): void => {
    this.locked = document.pointerLockElement === this.surface;
    // Releasing the pointer (alt-tab, Escape) must also release the keys, or
    // the player walks into a wall for as long as the tab is unfocused.
    if (!this.locked) this.clear();
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    this.held.add(event.code);
    this.latched |= (KEY_BITS[event.code] ?? 0) & TAP_BUTTONS;
    // Space scrolls the page and the arrow keys move focus; neither is wanted
    // while the pointer is captured.
    if (this.locked && (event.code === "Space" || event.code.startsWith("Arrow"))) {
      event.preventDefault();
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.held.delete(event.code);
  };

  private readonly onBlur = (): void => {
    this.clear();
  };

  private readonly onMouseDown = (event: MouseEvent): void => {
    if (event.button !== 0) return;
    this.firing = true;
    this.latched |= Btn.Fire;
  };

  private readonly onMouseUp = (event: MouseEvent): void => {
    if (event.button === 0) this.firing = false;
  };

  private readonly onMouseMove = (event: MouseEvent): void => {
    if (!this.locked) return;
    // Mouse right turns right, which is decreasing yaw: yaw grows
    // counter-clockwise viewed from above, and yaw 0 faces -Z.
    this.yaw -= event.movementX * this.sensitivity;
    this.pitch -= event.movementY * this.sensitivity;
    if (this.pitch > PITCH_LIMIT) this.pitch = PITCH_LIMIT;
    if (this.pitch < -PITCH_LIMIT) this.pitch = -PITCH_LIMIT;
  };

  private clear(): void {
    this.held.clear();
    this.firing = false;
    // A tap that has not been sampled yet is dropped along with everything
    // else. Alt-tabbing out mid-click and having the shot go off on the way
    // back is the same surprise as walking into a wall while unfocused, which
    // is what the rest of this method exists to prevent.
    this.latched = 0;
  }
}
